import * as React from 'react'
import { useTranslation } from 'react-i18next'

import {
  describeRoute,
  pickRouteEndpoints,
  stopLocationTypeAbbrKey,
  stopLocationTypeKey,
} from '#/entities/shipment'
import {
  LocationCard,
  ShipmentCard as ShipmentFieldsCard,
  useLocationForm,
  useSameAsCustomer,
  useShipmentForm,
} from '#/features/order-create'
import type {
  LocationFormData,
  ShipmentFormData,
} from '#/features/order-create'
import type {
  CustomerDetailBrief,
  StopResponse,
} from '#/shared/api/generated/shipment/models'
import { splitTimestamp } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { RouteStopsCombinedIcon } from '#/shared/ui/icons'
import type { MultiSelectOption } from '#/shared/ui/multi-select'
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '#/shared/ui/tooltip'
import { EditFooter } from './edit-footer'
import { SectionEditButton } from './section-edit-button'

interface RouteSectionProps {
  stops: StopResponse[]
  trailer?: string
  estimatedPickupAt?: string | null
  estimatedDeliveryAt?: string | null
  firstAvailableDate?: string | null
  editing: boolean
  onEditStart: () => void
  onCancel: () => void
  onSave: (data: {
    location: LocationFormData
    shipment: ShipmentFormData
  }) => void | Promise<void>
  locationDefaults: LocationFormData
  shipmentDefaults: ShipmentFormData
  tagSeedOptions?: MultiSelectOption[]
  compact?: boolean
  customer?: CustomerDetailBrief | null
  readOnly?: boolean
  editClassName?: string
  headerExtra?: React.ReactNode
  /** Drops the section's mx-2.5 / px-2.5 insets for flush sidebar layouts. */
  flush?: boolean
  /**
   * Overrides whether the LB / CM note inputs are hidden in the edit form.
   * Defaults to `!compact`. Pass `true` when notes are edited in their own
   * section (e.g. the sidebar's separate NotesSection).
   */
  hideNotes?: boolean
  /** Renders a "ROUTE" header on the edit form (sidebar gray-card style). */
  showEditTitle?: boolean
}

interface RouteStopDescriptor {
  stop: StopResponse | null
  address: string
  eta: string
  typeKey: string | undefined
  typeAbbrKey: string | undefined
}

function formatEtaDate(iso: string): string {
  return splitTimestamp(iso).date
}

function buildStopDescriptor(
  stop: StopResponse | null,
  address: string,
  scheduledIso: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
  emptyLabel: string,
): RouteStopDescriptor {
  return {
    stop,
    address: address || emptyLabel,
    eta: scheduledIso
      ? t('shipmentCard.est', { date: formatEtaDate(scheduledIso) })
      : '',
    typeKey: stopLocationTypeKey(stop),
    typeAbbrKey: stopLocationTypeAbbrKey(stop),
  }
}

function getStopLabels(
  desc: RouteStopDescriptor,
  compact: boolean | undefined,
  t: (key: string) => string,
): { typeLabel?: string; typeFullLabel?: string } {
  if (compact) return {}
  return {
    typeLabel: desc.typeAbbrKey ? t(desc.typeAbbrKey) : undefined,
    typeFullLabel: desc.typeKey ? t(desc.typeKey) : undefined,
  }
}

function RouteStopRow({
  typeLabel,
  typeFullLabel,
  address,
  eta,
  flush,
}: {
  typeLabel?: string
  typeFullLabel?: string
  address: string
  eta: string
  flush?: boolean
}) {
  return (
    <div className={cn('flex h-6 items-center gap-2.5', !flush && 'px-1.25')}>
      {typeLabel && (
        <TooltipProvider delayDuration={200}>
          <TooltipRoot>
            <TooltipTrigger asChild>
              <span className="flex h-6 cursor-default items-center rounded-md bg-gray-100 px-1.25 text-xs text-gray-900 select-none">
                {typeLabel}
              </span>
            </TooltipTrigger>
            {typeFullLabel && <TooltipContent>{typeFullLabel}</TooltipContent>}
          </TooltipRoot>
        </TooltipProvider>
      )}
      <span className="text-sm text-gray-900">{address}</span>
      {eta && (
        <>
          <span className="text-sm text-gray-500">·</span>
          <span className="text-sm text-gray-900">{eta}</span>
        </>
      )}
    </div>
  )
}

function RouteSectionHeader({
  trailer,
  headerExtra,
  flush,
}: {
  trailer?: string
  headerExtra?: React.ReactNode
  flush?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex h-6 items-center justify-between text-xs text-gray-500',
        !flush && 'px-2.5',
      )}
    >
      <span className="uppercase">{t('shipmentCard.route')}</span>
      <span className="flex items-center gap-2">
        {headerExtra}
        {trailer && <span>{t('shipmentCard.trailerValue', { trailer })}</span>}
      </span>
    </div>
  )
}

function useRouteRows({
  stops,
  estimatedPickupAt,
  estimatedDeliveryAt,
  firstAvailableDate,
  compact,
}: {
  stops: StopResponse[]
  estimatedPickupAt?: string | null
  estimatedDeliveryAt?: string | null
  firstAvailableDate?: string | null
  compact?: boolean
}) {
  const { t } = useTranslation()
  const route = describeRoute(stops)
  const { origin, destination } = pickRouteEndpoints(stops)
  const emptyLabel = t('shipmentCard.empty')
  const originDesc = buildStopDescriptor(
    origin,
    route.origin,
    compact ? null : estimatedPickupAt,
    t,
    emptyLabel,
  )
  const destDesc = buildStopDescriptor(
    destination,
    route.destination,
    compact ? null : estimatedDeliveryAt,
    t,
    emptyLabel,
  )
  const originEta =
    compact && firstAvailableDate
      ? t('shipmentCard.availFrom', { date: formatEtaDate(firstAvailableDate) })
      : originDesc.eta
  return {
    origin: {
      ...getStopLabels(originDesc, compact, t),
      address: originDesc.address,
      eta: originEta,
    },
    dest: {
      ...getStopLabels(destDesc, compact, t),
      address: destDesc.address,
      eta: destDesc.eta,
    },
  }
}

function RouteSectionView({
  stops,
  trailer,
  estimatedPickupAt,
  estimatedDeliveryAt,
  firstAvailableDate,
  compact,
  onEdit,
  headerExtra,
  flush,
}: {
  stops: StopResponse[]
  trailer?: string
  estimatedPickupAt?: string | null
  estimatedDeliveryAt?: string | null
  firstAvailableDate?: string | null
  compact?: boolean
  onEdit?: () => void
  headerExtra?: React.ReactNode
  flush?: boolean
}) {
  const { t } = useTranslation()
  const { origin, dest } = useRouteRows({
    stops,
    estimatedPickupAt,
    estimatedDeliveryAt,
    firstAvailableDate,
    compact,
  })

  return (
    <div
      className={cn(
        'group/section relative flex flex-col rounded-md',
        // flush (sidebar): pad inside the parent's inset so the hover bg keeps
        // an even margin from the panel edges instead of bleeding to them.
        flush ? 'px-2.5' : 'mx-2.5',
        onEdit && 'hover:bg-gray-100',
      )}
    >
      {onEdit && (
        <SectionEditButton
          label={t('shipmentCard.editRoute')}
          onClick={onEdit}
        />
      )}
      <RouteSectionHeader
        trailer={trailer}
        headerExtra={headerExtra}
        flush={flush}
      />
      <div className="flex flex-col gap-1">
        <div className={cn('flex items-center gap-1.5', !flush && 'px-2.5')}>
          <RouteStopsCombinedIcon className="h-16 w-5.5 shrink-0" />
          <div className="flex flex-1 flex-col gap-2.5">
            <RouteStopRow {...origin} flush={flush} />
            <RouteStopRow {...dest} flush={flush} />
          </div>
        </div>
      </div>
    </div>
  )
}

function useRouteEditSubmit(
  loc: ReturnType<typeof useLocationForm>,
  ship: ReturnType<typeof useShipmentForm>,
  onSave: (data: {
    location: LocationFormData
    shipment: ShipmentFormData
  }) => void | Promise<void>,
) {
  const [submitting, setSubmitting] = React.useState(false)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    const [locValid, shipValid] = await Promise.all([
      loc.trigger(),
      ship.trigger(),
    ])
    if (!locValid || !shipValid) return
    setSubmitting(true)
    try {
      await onSave({ location: loc.getValues(), shipment: ship.getValues() })
    } finally {
      setSubmitting(false)
    }
  }
  return { submitting, handleSubmit }
}

function RouteSectionEdit({
  locationDefaults,
  shipmentDefaults,
  tagSeedOptions,
  onCancel,
  onSave,
  compact,
  customer,
  editClassName,
  flush,
  hideNotes,
  showTitle,
}: {
  locationDefaults: LocationFormData
  shipmentDefaults: ShipmentFormData
  tagSeedOptions?: MultiSelectOption[]
  onCancel: () => void
  onSave: (data: {
    location: LocationFormData
    shipment: ShipmentFormData
  }) => void | Promise<void>
  compact?: boolean
  customer?: CustomerDetailBrief | null
  editClassName?: string
  flush?: boolean
  hideNotes?: boolean
  showTitle?: boolean
}) {
  const { t } = useTranslation()
  const loc = useLocationForm({ defaultValues: locationDefaults })
  const ship = useShipmentForm({ defaultValues: shipmentDefaults })
  const onSameAsCustomer = useSameAsCustomer(customer?.id, loc.setValue)
  const { submitting, handleSubmit } = useRouteEditSubmit(loc, ship, onSave)

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex flex-col rounded-md bg-gray-100',
        showTitle ? 'gap-1' : 'pt-1.25',
        !flush && 'mx-2.5',
        editClassName,
      )}
    >
      {showTitle && (
        <div className="flex h-6 items-center px-2.5">
          <span className="text-xs uppercase text-gray-500">
            {t('shipmentCard.route')}
          </span>
        </div>
      )}
      <div className="flex flex-col gap-5">
        <LocationCard
          bare
          cityZipOnly={compact}
          control={loc.control}
          register={loc.register}
          setValue={loc.setValue}
          errors={loc.errors}
          onSameAsCustomer={onSameAsCustomer}
          collapsibleContacts={showTitle}
        />
        <ShipmentFieldsCard
          bare
          hideNotes={hideNotes ?? !compact}
          hideTitle
          compact={compact}
          dateGrid={showTitle}
          control={ship.control}
          register={ship.register}
          errors={ship.errors}
          tagSeedOptions={tagSeedOptions}
        />
      </div>
      <EditFooter onCancel={onCancel} saveType="submit" disabled={submitting} />
    </form>
  )
}

export function RouteSection({
  stops,
  trailer,
  estimatedPickupAt,
  estimatedDeliveryAt,
  firstAvailableDate,
  editing,
  onEditStart,
  onCancel,
  onSave,
  locationDefaults,
  shipmentDefaults,
  tagSeedOptions,
  compact,
  customer,
  readOnly,
  editClassName,
  headerExtra,
  flush,
  hideNotes,
  showEditTitle,
}: RouteSectionProps) {
  if (editing && !readOnly) {
    return (
      <RouteSectionEdit
        locationDefaults={locationDefaults}
        shipmentDefaults={shipmentDefaults}
        tagSeedOptions={tagSeedOptions}
        onCancel={onCancel}
        onSave={onSave}
        compact={compact}
        customer={customer}
        editClassName={editClassName}
        flush={flush}
        hideNotes={hideNotes}
        showTitle={showEditTitle}
      />
    )
  }
  return (
    <RouteSectionView
      stops={stops}
      trailer={trailer}
      estimatedPickupAt={estimatedPickupAt}
      estimatedDeliveryAt={estimatedDeliveryAt}
      firstAvailableDate={firstAvailableDate}
      compact={compact}
      onEdit={readOnly ? undefined : onEditStart}
      headerExtra={headerExtra}
      flush={flush}
    />
  )
}
